import logger from '../utils/logger';
import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GlobalStateContext } from '../utils/GlobalStateContext';
import { getUserId, sanitizeDateForInput, uploadFollowUpNoteToBucket } from '../utils/utils';
import PageTopBar from './customComponents/PageTopBar';
import NewSignature from '../utils/NewSignature';
import RISOrderLabsModal from './customComponents/RISOrderLabsModal';
import RISOrderImagingModal from './customComponents/RISOrderImagingModal';
import { jsPDF } from 'jspdf';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../style/RISFollowUpNote.css';
import { Home, FileText } from 'lucide-react';

export default function RISFollowUpNote() {
  const { currentUser, currentPatient } = useContext(GlobalStateContext);
  const BASE_URL = process.env.REACT_APP_API_URL;
  const user = getUserId(currentUser);
  const [searchParams] = useSearchParams();

  const examId = searchParams.get('exam_id');
  const editingNoteId = searchParams.get('note_id');
  const isEditing = searchParams.get('editing') === 'true';

  const [noteId, setNoteId] = useState(null);
  const [noteStatus, setNoteStatus] = useState('Pending');
  const [noteText, setNoteText] = useState('');
  const [visitDate, setVisitDate] = useState(sanitizeDateForInput(new Date().toISOString()));
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [labOrders, setLabOrders] = useState([]);
  const [radOrders, setRadOrders] = useState([]);
  const [labModalOpen, setLabModalOpen] = useState(false);
  const [imagingModalOpen, setImagingModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [addendumText, setAddendumText] = useState('');
  const [showAddendum, setShowAddendum] = useState(false);

  const risPatientId = currentPatient?.ris_patients_id;
  const patientName = currentPatient
    ? `${currentPatient.first_name} ${currentPatient.last_name}`
    : 'Unknown Patient';
  const clinicianName = currentUser
    ? `${currentUser.first_name} ${currentUser.last_name}`
    : '';

  useEffect(() => {
    if (isEditing && editingNoteId) {
      loadExistingNote(editingNoteId);
    } else {
      createDraftNote();
    }
  }, []);

  const createDraftNote = async () => {
    try {
      const response = await fetch(`${BASE_URL}/orders/risFollowUpNote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rad_orders_id: examId || null,
          ris_patients_id: risPatientId,
          note: '',
          visit_date: visitDate,
          signed_at: null,
          status: 'Pending',
          user,
          type: currentUser?.type,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setNoteId(data.id);
      } else {
        toast.error(data.msg || 'Failed to initialize note');
      }
    } catch (err) {
      logger.error('Error creating draft note:', err);
      toast.error('Error initializing follow-up note');
    } finally {
      setIsLoading(false);
    }
  };

  const loadExistingNote = async (id) => {
    try {
      const response = await fetch(
        `${BASE_URL}/orders/risFollowUpNote?id=${id}&user=${user}&type=${currentUser?.type}`
      );
      const data = await response.json();
      if (response.ok && data.length) {
        const n = data[0];
        setNoteId(n.id);
        setNoteText(n.note || '');
        setVisitDate(sanitizeDateForInput(n.visit_date) || sanitizeDateForInput(new Date().toISOString()));
        setNoteStatus(n.status || 'Pending');
        await fetchOrders(n.id);
      }
    } catch (err) {
      logger.error('Error loading note:', err);
      toast.error('Error loading follow-up note');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrders = async (id) => {
    const nId = id || noteId;
    if (!nId) return;
    try {
      const response = await fetch(
        `${BASE_URL}/orders/risFollowUpNoteOrders?followup_note_id=${nId}&user=${user}&type=${currentUser?.type}`
      );
      const data = await response.json();
      if (response.ok) {
        setLabOrders(data.lab_orders || []);
        setRadOrders(data.radiology_orders || []);
      }
    } catch (err) {
      logger.error('Error fetching orders:', err);
    }
  };

  useEffect(() => {
    if (noteId) fetchOrders();
  }, [noteId]);

  const handleSaveDraft = async () => {
    if (!noteId) { toast.warning('Note is not initialized yet'); return; }
    setIsSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/orders/risFollowUpNote`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: noteId,
          note: noteText,
          visit_date: visitDate,
          signed_at: null,
          status: 'Pending',
          user,
          type: currentUser?.type,
          ris_patients_id: risPatientId,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Draft saved');
        setNoteStatus('Pending');
      } else {
        toast.error(data.msg || 'Failed to save draft');
      }
    } catch (err) {
      logger.error('Save draft error:', err);
      toast.error('Error saving draft');
    } finally {
      setIsSubmitting(false);
    }
  };

  const buildPDF = async (noteContent) => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 40;
    let y = margin;

    // Logo
    try {
      const imgRes = await fetch('/img/health_guardian.jpeg');
      const imgBlob = await imgRes.blob();
      const imgData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(imgBlob);
      });
      doc.addImage(imgData, 'JPEG', margin, y, 60, 60);
    } catch (e) {
      logger.warn('Logo load failed:', e);
    }

    doc.setFontSize(20);
    doc.setTextColor(0, 157, 134);
    doc.text('Follow-Up Consultation Note', margin + 70, y + 24);
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text('Health Guardian Imaging', margin + 70, y + 41);
    y += 74;

    doc.setDrawColor(0, 157, 134);
    doc.setLineWidth(1);
    doc.line(margin, y, pageW - margin, y);
    y += 16;

    doc.setFontSize(10);
    doc.setTextColor(30);

    const row = (label, value, x2, label2, value2) => {
      doc.setFont(undefined, 'bold');
      doc.text(label, margin, y);
      doc.setFont(undefined, 'normal');
      doc.text(String(value || ''), margin + 60, y);
      if (label2) {
        doc.setFont(undefined, 'bold');
        doc.text(label2, x2, y);
        doc.setFont(undefined, 'normal');
        doc.text(String(value2 || ''), x2 + 65, y);
      }
      y += 17;
    };

    row('Patient:', patientName, margin + 260, 'Clinician:', clinicianName);
    row('Visit Date:', visitDate || '', margin + 260, 'Note ID:', noteId);
    row('Status:', 'Final', null, null, null);
    y += 4;

    doc.setDrawColor(220);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageW - margin, y);
    y += 16;

    // Note body
    const addSection = (title, lines) => {
      if (y > pageH - 80) { doc.addPage(); y = margin; }
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 157, 134);
      doc.text(title, margin, y);
      y += 15;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      doc.setTextColor(30);
      lines.forEach(line => {
        if (y > pageH - 40) { doc.addPage(); y = margin; }
        doc.text(line, margin, y);
        y += 13;
      });
      y += 8;
    };

    addSection(
      'Clinical Note',
      doc.splitTextToSize(noteContent || '(No note content)', pageW - margin * 2)
    );

    if (labOrders.length > 0) {
      addSection(
        'Lab Orders',
        labOrders.map(o => `• ${o.exam_ordered || ''} — ${o.specimen_type || ''} — ${o.priority || ''}`)
      );
    }

    if (radOrders.length > 0) {
      addSection(
        'Imaging Orders',
        radOrders.map(o => `• ${o.exam_ordered || ''} — ${o.modality || ''} — ${o.priority || ''}`)
      );
    }

    // Signature
    if (signatureUrl) {
      if (y > pageH - 100) { doc.addPage(); y = margin; }
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 157, 134);
      doc.text('Clinician Signature', margin, y);
      y += 12;
      try {
        doc.addImage(signatureUrl, 'PNG', margin, y, 200, 60);
        y += 70;
      } catch (e) {
        logger.warn('Signature image failed:', e);
      }
    }

    if (y > pageH - 30) { doc.addPage(); y = margin; }
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(140);
    doc.text(
      `Signed: ${new Date().toLocaleString()}  |  ${clinicianName}`,
      margin, y
    );

    return doc;
  };

  const handleFinalize = async () => {
    if (!noteId) { toast.warning('Note is not initialized yet'); return; }
    if (!signatureUrl) { toast.warning('Please provide a signature before finalizing'); return; }

    setIsSubmitting(true);
    const signedAt = new Date().toISOString();

    try {
      const patchRes = await fetch(`${BASE_URL}/orders/risFollowUpNote`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: noteId,
          note: noteText,
          visit_date: visitDate,
          signed_at: signedAt,
          status: 'Final',
          user,
          type: currentUser?.type,
          ris_patients_id: risPatientId,
        }),
      });
      const patchData = await patchRes.json();
      if (!patchRes.ok) {
        toast.error(patchData.msg || 'Failed to finalize note');
        setIsSubmitting(false);
        return;
      }

      const doc = await buildPDF(noteText);
      const pdfBlob = doc.output('blob');
      await uploadFollowUpNoteToBucket(pdfBlob, noteId, currentUser?.type, user);

      toast.success('Follow-up note finalized and saved');
      setNoteStatus('Final');
    } catch (err) {
      logger.error('Finalize error:', err);
      toast.error('Error finalizing note. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddendum = async () => {
    if (!addendumText.trim()) { toast.warning('Please enter addendum text'); return; }
    if (!signatureUrl) { toast.warning('Please provide a signature for the addendum'); return; }

    setIsSubmitting(true);
    const signedAt = new Date().toISOString();
    const combined =
      noteText +
      '\n\n[Addendum — ' + new Date().toLocaleString() + ']\n' +
      addendumText;

    try {
      const patchRes = await fetch(`${BASE_URL}/orders/risFollowUpNote`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: noteId,
          note: combined,
          visit_date: visitDate,
          signed_at: signedAt,
          status: 'Addendum',
          user,
          type: currentUser?.type,
          ris_patients_id: risPatientId,
        }),
      });
      const patchData = await patchRes.json();
      if (!patchRes.ok) {
        toast.error(patchData.msg || 'Failed to save addendum');
        setIsSubmitting(false);
        return;
      }

      const doc = await buildPDF(combined);
      const pdfBlob = doc.output('blob');
      await uploadFollowUpNoteToBucket(pdfBlob, noteId, currentUser?.type, user);

      toast.success('Addendum saved');
      setNoteText(combined);
      setNoteStatus('Addendum');
      setAddendumText('');
      setShowAddendum(false);
    } catch (err) {
      logger.error('Addendum error:', err);
      toast.error('Error saving addendum. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const topBarLinks = [
    { label: 'Home', to: '/home', icon: Home },
    { label: 'Radiology', to: '/radiology', icon: FileText },
  ];

  if (isLoading) {
    return (
      <div>
        <PageTopBar links={topBarLinks} />
        <div className="rfn-loading">Initializing note...</div>
      </div>
    );
  }

  const isFinal = noteStatus === 'Final' || noteStatus === 'Addendum';

  return (
    <div className="rfn-page">
      <PageTopBar links={topBarLinks} />

      <div className="rfn-container">
        {/* Header */}
        <div className="rfn-header">
          <div>
            <h1 className="rfn-title">Follow-Up Consultation Note</h1>
            <p className="rfn-patient">{patientName}</p>
          </div>
          <span className={`rfn-status rfn-status-${noteStatus?.toLowerCase()}`}>
            {noteStatus}
          </span>
        </div>

        {/* Visit Date */}
        <div className="rfn-field">
          <label className="rfn-label">Visit Date</label>
          <input
            type="date"
            className="rfn-input"
            value={visitDate}
            onChange={e => setVisitDate(e.target.value)}
            disabled={isFinal || isSubmitting}
          />
        </div>

        {/* Clinical Note */}
        <div className="rfn-field">
          <label className="rfn-label">Clinical Note</label>
          <textarea
            className="rfn-textarea"
            rows={10}
            value={noteText}
            onChange={e => setNoteText(e.target.value)}
            placeholder="Enter clinical note..."
            disabled={isFinal || isSubmitting}
          />
        </div>

        {/* Orders */}
        <div className="rfn-section">
          <div className="rfn-section-header">
            <h2 className="rfn-section-title">Orders</h2>
            {!isFinal && (
              <div className="rfn-order-btns">
                <button
                  className="rfn-order-btn"
                  onClick={() => setLabModalOpen(true)}
                  disabled={!noteId || isSubmitting}
                >
                  + Order Labs
                </button>
                <button
                  className="rfn-order-btn"
                  onClick={() => setImagingModalOpen(true)}
                  disabled={!noteId || isSubmitting}
                >
                  + Order Imaging
                </button>
              </div>
            )}
          </div>

          {labOrders.length === 0 && radOrders.length === 0 ? (
            <p className="rfn-empty">No orders placed yet.</p>
          ) : (
            <>
              {labOrders.length > 0 && (
                <div className="rfn-orders-group">
                  <p className="rfn-orders-group-title">Lab Orders</p>
                  {labOrders.map((o, i) => (
                    <div className="rfn-order-item" key={i}>
                      <span className="rfn-order-name">{o.exam_ordered}</span>
                      <span className="rfn-order-meta">{o.specimen_type}</span>
                      <span className={`rfn-order-priority rfn-priority-${(o.priority || 'routine').toLowerCase().replace(/\s+/g, '-')}`}>
                        {o.priority}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {radOrders.length > 0 && (
                <div className="rfn-orders-group">
                  <p className="rfn-orders-group-title">Imaging Orders</p>
                  {radOrders.map((o, i) => (
                    <div className="rfn-order-item" key={i}>
                      <span className="rfn-order-name">{o.exam_ordered}</span>
                      <span className="rfn-order-meta">{o.modality}</span>
                      <span className={`rfn-order-priority rfn-priority-${(o.priority || 'routine').toLowerCase().replace(/\s+/g, '-')}`}>
                        {o.priority}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Signature (for new/pending note) */}
        {!isFinal && (
          <div className="rfn-section">
            <h2 className="rfn-section-title">Clinician Signature</h2>
            <p className="rfn-sig-hint">Required to finalize the note.</p>
            <NewSignature onSignatureSave={setSignatureUrl} />
          </div>
        )}

        {/* Action buttons */}
        {!isFinal && (
          <div className="rfn-actions">
            <button
              className="rfn-btn-secondary"
              onClick={handleSaveDraft}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              className="rfn-btn-primary"
              onClick={handleFinalize}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Finalizing...' : 'Finalize & Sign'}
            </button>
          </div>
        )}

        {/* Addendum section (only for Final/Addendum notes) */}
        {isFinal && (
          <div className="rfn-section">
            {!showAddendum ? (
              <button className="rfn-order-btn" onClick={() => setShowAddendum(true)}>
                Add Addendum
              </button>
            ) : (
              <>
                <h2 className="rfn-section-title">Addendum</h2>
                <textarea
                  className="rfn-textarea"
                  rows={5}
                  value={addendumText}
                  onChange={e => setAddendumText(e.target.value)}
                  placeholder="Enter addendum text..."
                  disabled={isSubmitting}
                />
                <div className="rfn-section rfn-addendum-sig">
                  <h3 className="rfn-section-title">Signature</h3>
                  <NewSignature onSignatureSave={setSignatureUrl} />
                </div>
                <div className="rfn-actions">
                  <button
                    className="rfn-btn-secondary"
                    onClick={() => { setShowAddendum(false); setAddendumText(''); }}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    className="rfn-btn-primary"
                    onClick={handleAddendum}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : 'Save Addendum'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <RISOrderLabsModal
        isOpen={labModalOpen}
        onClose={() => setLabModalOpen(false)}
        onOrderPlaced={fetchOrders}
        followupNoteId={noteId}
        risPatientId={risPatientId}
      />
      <RISOrderImagingModal
        isOpen={imagingModalOpen}
        onClose={() => setImagingModalOpen(false)}
        onOrderPlaced={fetchOrders}
        followupNoteId={noteId}
        risPatientId={risPatientId}
      />

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
    </div>
  );
}
